import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogTitle, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { ProjectConfiguration } from '@models/ProjectConfiguration';
import { PARSING_FORMAT_TEMPLATES, ParsingFormat, ParsingFormatFactory, parsingFormatFromId, ParsingFormatTemplate } from '@models/parsing-formats/ParsingFormat';
import { Canon } from '@models/Canon';
import { ProjectTextStyleDirective } from '../../project-text-style.directive';
import { ParsingTestDisplayComponent } from '../parsing-test-display/parsing-test-display.component';
import { MatSelectModule } from '@angular/material/select';
import { ConfirmationDialogComponent, ConfirmationDialogData } from 'src/app/confirmation-dialog/confirmation-dialog.component';

export interface ParsingFormatEditorInput {
  canon: Canon;
  project: ProjectConfiguration;
  parsingFormat?: ParsingFormat;
}

@Component({
  selector: 'app-parsing-format-editor',
  imports: [CommonModule,
    ProjectTextStyleDirective,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatStepperModule,
    MatCardModule,
    MatListModule,
    MatIconModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatSelectModule,
    ParsingTestDisplayComponent],
  templateUrl: './parsing-format-editor.component.html',
  styleUrl: './parsing-format-editor.component.scss'
})
export class ParsingFormatEditorComponent implements AfterViewInit {
  @ViewChild('stepper') stepper!: MatStepper; // Reference to MatStepper
  readonly dialogRef = inject(MatDialogRef<ParsingFormatEditorComponent, ParsingFormat>);
  readonly data = inject<ParsingFormatEditorInput>(MAT_DIALOG_DATA);
  private _formBuilder = inject(FormBuilder);
  project: ProjectConfiguration;
  canon: Canon;
  template_id: string | undefined;
  parsingFormatTemplate: ParsingFormatTemplate | undefined;
  parsingFormat: ParsingFormat | undefined;
  translationsGroup: FormGroup;
  initialPanel: FormGroup;
  namePanel: FormGroup;
  // id: string = '';
  strings: { [key: string]: string } = {};

  constructor(public dialog: MatDialog) {
    this.canon = this.data.canon;
    this.project = this.data.project;
    if (this.data.parsingFormat) {
      this.parsingFormat = this.data.parsingFormat;
      this.template_id = this.parsingFormat.template;
    }

    this.translationsGroup = this._formBuilder.group({});
    this.initialPanel = this._formBuilder.group({});
    this.initialPanel = this._formBuilder.group({
      template: new FormControl('', Validators.required)
    });
    this.namePanel = this._formBuilder.group({
      id: new FormControl('', Validators.required),
    });
  }

  ngAfterViewInit() {
    if (this.parsingFormat) {
      this.initialPanel.patchValue({
        template: [this.parsingFormat.template]
      });
      this.namePanel.patchValue({
        id: this.parsingFormat.id,
      });
    }
  }


  onDismiss(): void {
    const data: ConfirmationDialogData = {
      title: `Confirm close`,
      message: `Are you sure you want to lose the data you've entered so far?`,
      yes: `Discard Data`,
      no: 'Stay Open'
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: data
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dialogRef.close();
      }
    });


  }

  accept(): void {
    if (this.parsingFormat) {
      this.dialogRef.close(this.parsingFormat);
    }
  }

  afterFirstPanel() {
    // this.id = this.initialPanel.get('id')?.value;
    this.template_id = this.initialPanel.get('template')?.value[0];
    if (this.template_id) {
      this.selectTemplate(this.template_id);
    }
    if (!this.parsingFormat) {
      this.namePanel.get('id')?.setValue(this.types.filter((value: ParsingFormatTemplate) => value.id === this.template_id)[0].name);
    }
  }

  get types(): ParsingFormatTemplate[] {
    return PARSING_FORMAT_TEMPLATES.filter((value: ParsingFormatTemplate) => value.canon === this.canon);
  }

  selectTemplate(id: string) {
    this.parsingFormatTemplate = parsingFormatFromId(id);
    if (this.parsingFormatTemplate) {
      this.translationsGroup = this._formBuilder.group({});
      this.stringKeys.forEach((key: string) => {

        if (this.parsingFormatTemplate?.placeholders) {
          const value = this.parsingFormat ? this.parsingFormat.getString(key) : this.parsingFormatTemplate.placeholders[key];
          this.translationsGroup.addControl(key, this._formBuilder.control(value, Validators.required));
        }
      });
    }
  }

  get stringKeys(): string[] {
    if (this.parsingFormatTemplate) {
      return Object.keys(this.parsingFormatTemplate.strings);
    }
    return [];
  }

  createParsingFormat() {
    this.strings = {};
    this.stringKeys.forEach((key: string) => {
      this.strings[key] = this.translationsGroup.get(key)?.value || '';
    });
    if (this.parsingFormatTemplate) {
      this.parsingFormat = ParsingFormatFactory.createParsingFormat(this.namePanel.get('id')?.value, this.project.replaceNumerals.bind(this.project), this.parsingFormatTemplate.id, this.strings);
    }
  }

}
